import { browser } from '$app/environment';
import { writable } from 'svelte/store';
import { firebaseConfig, VAPID_KEY } from '$lib/config/firebase-config.js';
import { apiFetch } from '$lib/services/api-fetch.js';
import { API_ENDPOINTS, HTTP_HEADERS } from '$lib/config/api-endpoints.js';
import { authStore } from '$lib/stores/auth.js';
import { featureFlags } from '$lib/stores/feature-flags.js';

/** Kill-switch: l'intera feature è dietro il flag `web-push-notifications`. */
function isFeatureEnabled() {
  return browser && featureFlags.isOn('web-push-notifications');
}

/**
 * Servizio notifiche push web (Firebase Cloud Messaging) — solo lato client.
 * Destinatari: utenti loggati. Il token viene registrato sul backend esistente
 * (PUT /users/me/notification-token), che lo salva in `core_utentetoken`.
 */

const SW_URL = '/firebase-messaging-sw.js';
const SW_SCOPE = '/firebase-cloud-messaging-push-scope';

/** Messaggio ricevuto mentre il sito è aperto (foreground), consumato dal toast. */
export const foregroundMessage = writable(null);

let messagingInstance = null;
let foregroundHandlerSet = false;

async function getMessagingInstance() {
  if (!browser) return null;
  if (messagingInstance) return messagingInstance;
  const { initializeApp, getApps } = await import('firebase/app');
  const { getMessaging, isSupported } = await import('firebase/messaging');
  if (!(await isSupported())) return null;
  const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

/** True se il browser/dispositivo supporta le push web e la feature è abilitata. */
export async function isPushSupported() {
  if (!browser) return false;
  if (!isFeatureEnabled()) return false;
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false;
  }
  try {
    const { isSupported } = await import('firebase/messaging');
    return await isSupported();
  } catch (e) {
    return false;
  }
}

/** Stato del permesso: 'unsupported' | 'default' | 'granted' | 'denied'. */
export function getPermissionState() {
  if (!browser || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

async function registerFirebaseSW() {
  const cfg = encodeURIComponent(JSON.stringify(firebaseConfig));
  return navigator.serviceWorker.register(`${SW_URL}?cfg=${cfg}`, { scope: SW_SCOPE });
}

async function setForegroundHandler(messaging) {
  if (foregroundHandlerSet) return;
  const { onMessage } = await import('firebase/messaging');
  onMessage(messaging, (payload) => {
    console.log('[push] foreground payload', payload);
    const n = payload.notification || {};
    const d = payload.data || {};
    // Mirror dell'app: corpo da notification.body oppure dal campo "content" (notifica dinamica),
    // link da fcmOptions.link / data.link / data.action.
    foregroundMessage.set({
      title: n.title || d.title || 'Auting',
      body: n.body || d.content || d.message || d.body || '',
      image: n.image || d.image || d.imageUrl || '',
      link: (payload.fcmOptions && payload.fcmOptions.link) || d.link || d.action || '',
      id: Date.now()
    });
  });
  foregroundHandlerSet = true;
}

async function acquireToken() {
  const messaging = await getMessagingInstance();
  if (!messaging) return null;
  const { getToken } = await import('firebase/messaging');
  const swReg = await registerFirebaseSW();
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: swReg
  });
  // Copia comoda per test/debug: leggibile da console con localStorage.getItem('fcm_token')
  try { if (token) localStorage.setItem('fcm_token', token); } catch (e) {}
  await setForegroundHandler(messaging);
  return token;
}

/** Invia il token FCM al backend (solo se l'utente è loggato). */
export async function registerToken(token) {
  if (!browser || !token) return { ok: false, reason: 'no-token' };
  const jwt = authStore.getToken();
  if (!jwt) return { ok: false, reason: 'not-authenticated' };
  try {
    const res = await apiFetch(API_ENDPOINTS.USER.NOTIFICATION_TOKEN, {
      method: 'PUT',
      headers: { ...HTTP_HEADERS.JSON, Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ token })
    });
    if (!res.ok) {
      let bodyText = '';
      try { bodyText = await res.text(); } catch (_) {}
      console.error('[push] registerToken HTTP error', res.status, API_ENDPOINTS.USER.NOTIFICATION_TOKEN, bodyText);
      return { ok: false, reason: `http-${res.status}` };
    }
    return { ok: true };
  } catch (e) {
    console.error('[push] registerToken network error', e);
    return { ok: false, reason: e?.message || 'network' };
  }
}

/** Chiede il permesso, ottiene il token e lo registra sul backend. */
export async function enablePush() {
  if (!isFeatureEnabled()) return { ok: false, reason: 'feature-disabled' };
  if (!(await isPushSupported())) return { ok: false, reason: 'unsupported' };
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: permission };
  let token;
  try {
    token = await acquireToken();
  } catch (e) {
    console.error('[push] getToken/SW error', e);
    return { ok: false, reason: e?.message || 'token-error' };
  }
  if (!token) {
    console.error('[push] getToken returned empty token');
    return { ok: false, reason: 'no-token' };
  }
  console.log('[push] FCM token acquired:', token);
  const reg = await registerToken(token);
  return reg.ok ? { ok: true, token } : { ok: false, reason: reg.reason };
}

/** Elimina il token lato client. (Non esiste endpoint di deregistrazione: il token
 *  diventa invalido lato FCM e gli invii successivi falliscono in modo innocuo.) */
export async function disablePush() {
  if (!browser) return;
  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return;
    const { deleteToken } = await import('firebase/messaging');
    await deleteToken(messaging);
  } catch (e) {
    // best-effort
  }
}

/** Al caricamento: se il permesso è già concesso e l'utente è loggato, ri-registra il token
 *  (i token FCM ruotano; la registrazione è idempotente lato backend). */
export async function syncOnLoad() {
  if (!browser) return;
  if (!isFeatureEnabled()) return;
  if (getPermissionState() !== 'granted') return;
  if (!authStore.getToken()) return;
  try {
    const token = await acquireToken();
    if (token) await registerToken(token);
  } catch (e) {
    // best-effort
  }
}
